"use client";

import type { ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { CircleHelp } from "lucide-react";
import { cn } from "@/lib/utils";

export const PixelCorners = ({
  color = "bg-spicenet-primary",
}: {
  color?: string;
}) => (
  <>
    <span className={cn("absolute left-3 top-3 h-1.5 w-1.5", color)} />
    <span className={cn("absolute right-3 top-3 h-1.5 w-1.5", color)} />
    <span className={cn("absolute bottom-3 left-3 h-1.5 w-1.5", color)} />
    <span className={cn("absolute bottom-3 right-3 h-1.5 w-1.5", color)} />
  </>
);

const DotTexture = ({
  color = "#EA5555",
  className,
}: {
  color?: string;
  className?: string;
}) => (
  <div
    aria-hidden="true"
    className={cn("pointer-events-none absolute inset-0 opacity-70", className)}
    style={{
      backgroundImage: `radial-gradient(${color} 1.2px, transparent 1.2px)`,
      backgroundSize: "10px 10px",
    }}
  />
);

const SpicenetEdgeMark = ({
  side,
  src = "/assets/campaigns/spicenet-edge-mark.png",
  flipX = side === "right",
  className,
}: {
  side: "left" | "right";
  src?: string;
  flipX?: boolean;
  className?: string;
}) => (
  <Image
    aria-hidden="true"
    src={src}
    alt=""
    width={312}
    height={507}
    className={cn(
      "pointer-events-none absolute top-24 z-[1] h-[360px] w-[222px] max-w-none object-contain opacity-80 mix-blend-screen sm:top-16 sm:h-[430px] sm:w-[265px] sm:opacity-100 md:top-14 md:h-[507px] md:w-[312px]",
      side === "left" ? "-left-10 sm:left-0" : "right-0 hidden sm:block",
      flipX && "scale-x-[-1]",
      className,
    )}
    priority
  />
);

export const CampaignHeader = ({
  title,
  description,
  children,
  variant = "red",
  campaignName,
  badgeLabel,
  statusText,
}: {
  title: ReactNode;
  description: ReactNode;
  children?: ReactNode;
  variant?: "red" | "dark";
  campaignName?: string;
  badgeLabel?: string;
  statusText?: string;
}) => {
  const isRed = variant === "red";
  return (
    <section
      className={cn(
        "relative overflow-hidden px-5 py-5",
        isRed
          ? "min-h-[390px] md:min-h-[470px]"
          : "min-h-[400px] md:min-h-[520px]",
        isRed
          ? "bg-spicenet-primary text-spicenet-black"
          : "bg-[#201f1d] text-white",
      )}
    >
      {!isRed && (
        <div className="absolute inset-x-0 top-0 z-10 flex h-14 items-center justify-center bg-spicenet-black font-mono text-sm font-medium tracking-[0.08em] text-white">
          <span className="inline-flex items-center gap-2 text-spicenet-primary">
            <span className="h-2.5 w-2.5 rounded-full bg-spicenet-primary" />
            {badgeLabel}
          </span>
          <span className="mx-5 text-white">Spice Flow x {campaignName}</span>
          <span className="text-white/70">· {statusText}</span>
        </div>
      )}

      <DotTexture
        color={isRed ? "rgba(255,255,255,0.23)" : "rgba(234,85,85,0.55)"}
        className="top-14"
      />
      {!isRed && (
        <>
          <SpicenetEdgeMark side="left" />
          <SpicenetEdgeMark side="right" />
        </>
      )}

      <div
        className={cn(
          "relative z-10 mx-auto flex max-w-4xl flex-col items-center justify-center text-center",
          isRed ? "min-h-[350px] pt-4 md:min-h-[430px]" : "min-h-[340px] pt-14",
        )}
      >
        <h1
          className={cn(
            "max-w-[calc(100vw-2.5rem)] text-3xl font-medium leading-tight md:text-5xl",
            isRed ? "text-white" : "text-white",
          )}
        >
          {title}
        </h1>
        <p
          className={cn(
            "mt-5 max-w-sm font-mono text-sm font-semibold leading-7 md:max-w-2xl md:text-lg",
            isRed ? "text-spicenet-black" : "text-white",
          )}
        >
          {description}
        </p>

        {children}
      </div>
    </section>
  );
};

export const PartnerHero = ({
  title,
  description,
  children,
  rightEdgeMark,
}: {
  title: ReactNode;
  description: ReactNode;
  children?: ReactNode;
  rightEdgeMark?: string;
}) => (
  <section className="relative min-h-[430px] overflow-hidden bg-[#201f1d] px-5 py-5 text-white md:min-h-[520px]">
    <DotTexture color="rgba(234,85,85,0.55)" />
    <SpicenetEdgeMark side="left" />
    <SpicenetEdgeMark side="right" src={rightEdgeMark} flipX={!rightEdgeMark} />

    <div className="relative z-10 mx-auto flex min-h-[390px] max-w-4xl flex-col items-center justify-center text-center md:min-h-[480px]">
      <h1 className="max-w-[calc(100vw-2.5rem)] text-3xl font-medium leading-tight md:text-5xl">
        {title}
      </h1>
      <p className="mt-5 max-w-[620px] font-mono text-sm font-semibold leading-7 text-white md:text-lg">
        {description}
      </p>

      {children}
    </div>
  </section>
);

const CardCornerMarks = () => (
  <>
    <span className="absolute left-2.5 top-2.5 font-mono text-sm leading-none text-spicenet-gray">
      +
    </span>
    <span className="absolute right-2.5 top-2.5 font-mono text-sm leading-none text-spicenet-gray">
      +
    </span>
    <span className="absolute bottom-2.5 left-2.5 font-mono text-sm leading-none text-spicenet-gray">
      +
    </span>
    <span className="absolute bottom-2.5 right-2.5 font-mono text-sm leading-none text-spicenet-gray">
      +
    </span>
  </>
);

export const CampaignCard = ({
  href,
  title,
  description,
  icon,
  children,
}: {
  href?: string;
  title: string;
  description: string;
  icon: ReactNode;
  children?: ReactNode;
}) => {
  const cardContent = (
    <>
      <CardCornerMarks />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage: `radial-gradient(rgba(255,255,255,0.3) 1px, transparent 1px)`,
          backgroundSize: "13px 13px",
        }}
      />
      <div className="relative z-10">
        {icon}
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="mt-2 max-w-xs text-sm leading-5 text-white/70">
          {description}
        </p>
        {children}
      </div>
    </>
  );

  if (!href) {
    return (
      <div
        aria-disabled="true"
        className="relative overflow-hidden border border-spicenet-primary/60 bg-spicenet-dark p-6 text-white"
      >
        {cardContent}
      </div>
    );
  }

  return (
    <Link
      href={href}
      className="relative overflow-hidden border border-spicenet-primary/60 bg-spicenet-dark p-6 text-white transition-transform duration-200 ease-out hover:scale-[1.02]"
    >
      {cardContent}
    </Link>
  );
};

export const SupportedNetworks = ({
  networks,
  description,
}: {
  networks: readonly string[];
  description?: string;
}) => (
  <div className="mt-12">
    <p className="mb-4 text-sm font-semibold text-spicenet-gray">
      Supported Networks
      <CircleHelp className="ml-2 inline h-4 w-4" />
    </p>
    {description && (
      <p className="mb-4 text-sm leading-6 text-spicenet-gray">{description}</p>
    )}
    <div className="flex flex-wrap gap-3">
      {networks.map((network) => (
        <span
          key={network}
          className="bg-spicenet-primary px-5 py-3 text-sm font-semibold text-white"
        >
          {network}
        </span>
      ))}
    </div>
  </div>
);
